import type { ReactNode } from 'react';
import { Tabs as HeroTabs } from '@heroui/react/tabs';
import { cn } from '@/utils/cn';

/**
 * The one tab strip. Built on HeroUI so keyboard navigation (arrow keys,
 * Home/End), roving focus and the `role="tablist"` semantics are handled
 * properly, replacing the hand-rolled `<button role="tab">` strips that had
 * drifted apart across Finance, Labs, Patients and Settings.
 *
 * The list always renders inside `Tabs.ListContainer`, which gives every
 * strip the mobile behaviour: it scrolls horizontally instead of wrapping,
 * and chevron arrows appear at the edges once the tabs overflow — they stay
 * hidden when everything fits. The arrows flip automatically under `dir="rtl"`.
 */
export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  icon?: ReactNode;
  /** Trailing count pill (e.g. number of pending items). Hidden when empty. */
  badge?: ReactNode | null;
  isDisabled?: boolean;
  /**
   * Per-tab classes, for the rare strip whose tabs carry their own meaning —
   * a status filter where each status owns a colour. Plain tab strips leave
   * these alone and inherit the variant.
   */
  className?: string;
  /** Classes applied only while this tab is the selected one. */
  activeClassName?: string;
}

export interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * `primary` — filled pill on a tinted track (default, use for view switches).
   * `secondary` — underlined tab, for section navigation inside a card.
   */
  variant?: 'primary' | 'secondary';
  /** Stretch the tabs to fill the row instead of hugging their labels. */
  fullWidth?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Anchor for a guided tour step (see `components/tour`). */
  'data-tour'?: string;
  /** Panel content for the selected tab. Omit to control content yourself. */
  children?: ReactNode;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  variant = 'primary',
  fullWidth = false,
  className,
  'aria-label': ariaLabel,
  'data-tour': tourAnchor,
  children,
}: TabsProps<T>) {
  return (
    <HeroTabs.Root
      data-tour={tourAnchor}
      variant={variant}
      selectedKey={value}
      onSelectionChange={(key) => onChange(key as T)}
      className={cn(fullWidth ? 'w-full' : 'w-fit max-w-full', className)}
    >
      <HeroTabs.ListContainer className={cn(fullWidth && 'w-full')}>
        <HeroTabs.List aria-label={ariaLabel}>
          {items.map((item) => (
            <HeroTabs.Tab
              key={item.id}
              id={item.id}
              isDisabled={item.isDisabled}
              className={cn(
                'gap-1.5 whitespace-nowrap',
                !fullWidth && 'w-auto',
                item.className,
                item.id === value && item.activeClassName,
              )}
            >
              {/* The indicator is a shared element that animates between the
                  tabs, so it lives inside each Tab — rendering it as a sibling
                  of the tabs throws out of <SharedElementTransition>. */}
              <HeroTabs.Indicator />
              {item.icon}
              {item.label}
              {item.badge !== undefined &&
              item.badge !== null &&
              item.badge !== '' ? (
                <span className="bg-default text-muted ms-0.5 rounded-full px-1.5 text-micro leading-4 num">
                  {item.badge}
                </span>
              ) : null}
            </HeroTabs.Tab>
          ))}
        </HeroTabs.List>
      </HeroTabs.ListContainer>
      {children}
    </HeroTabs.Root>
  );
}

/** Panel for one tab's content. `id` must match the `TabItem.id`. */
Tabs.Panel = function TabsPanel({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <HeroTabs.Panel id={id} className={className}>
      {children}
    </HeroTabs.Panel>
  );
};
